<template>
  <div>
    <aw-spinner />
  </div>
</template>

<script setup lang="ts">
  import { onMounted } from 'vue';
  import { navigateTo, useAsyncData, useNuxtApp, useRoute } from 'nuxt/app';
  import { fetchKey } from '~~/common/utils/awRunLegacyAsyncData';
  import { getLocaleCodeForUrl } from '~~/common/utils/localeCode';
  import { PM_CREDIT } from '~~/common/config/PaymentMethod';
  import type { Schema } from '~~/common/types/api';
  import { useOrderStore } from '~~/common/stores/order';
  import { useCheckoutStore } from '~~/common/stores/checkout';
  import AwSpinner from '~~/common/components/Common/AwSpinner.vue';

  defineOptions({
    name: 'CetelemPaymentReturn',
  });

  const { $i18n, $api, $logger, $localePath } = useNuxtApp();
  const route = useRoute();
  const {
    status,
    dossierId,
    orderId,
  } = route.query;
  const orderStore = useOrderStore();
  const checkoutStore = useCheckoutStore();

  const { data } = await useAsyncData(
    fetchKey(),
    async () => {
      try {
        await $api.$post('/cetelem/credit_payment_return', {
          orderIdent: orderId,
        });
        const successOrderCart = await $api.$get<Schema<'CartModel'>>(`/orders/${orderId}/cart`);
        const paymentData = await $api.$get<Schema<'PaymentTransactionModel'>[]>(`/orders/${orderId}/payment_transactions`);

        const checkoutType = successOrderCart.splits[0]?.checkoutType;

        const cartData = successOrderCart.splits[0];
        orderStore.setPaymentData({
          paymentData,
          checkoutType,
        });
        await orderStore.fetchOrder({
          id: 'current',
          fetchNonfoodCheckoutIfNFCartNotEmpty: false,
          fetchFoodCheckout: false,
        });
        await checkoutStore.fetchCheckout({
          type: checkoutType,
          canStartSecondAction: true,
          canRunSimultaneous: true,
        });

        (checkoutType === 'FOOD' ? orderStore.setCompletedFoodOrder : orderStore.setCompletedNonFoodOrder)({
          ...cartData,
          orderIdent: String(orderId),
          showCardInfo: true,
          creditStatus: status,
          creditDossierId: dossierId,
          paymentMethod: PM_CREDIT,
        });

        return {
          orderIdent: String(orderId),
          checkoutType: checkoutType?.toLowerCase() ?? null,
          creditStatus: status,
          creditDossierId: dossierId,
          paymentMethod: PM_CREDIT,
        };
      } catch (e) {
        $logger.error(e);
        return null;
      }
    },
    {
      default: () => null,
    },
  );

  if (!data.value?.orderIdent) {
    await navigateTo(`/${getLocaleCodeForUrl({ $i18n })}`);
  }

  onMounted(() => {
    if (!data.value?.orderIdent) {
      return;
    }
    navigateTo({
      path: $localePath(`/shop/checkout-success/${data.value.orderIdent}`),
      query: {
        creditStatus: data.value.creditStatus,
        ...(data.value.creditDossierId ? { creditDossierId: data.value.creditDossierId } : {}),
        ...(orderStore.editedOrder ? { edited: 'true' } : {}),
      },
    });
  });
</script>
